<template>
  <i-modal :style="{maxWidth: '660px'}" @openAutoFocus="getDocumentsList" :closeOnClickAway="false">
    <template #header>
      <div class="flex justify-between">
        <span class="title flex-auto">{{ title }}</span>
        <i-button :disabled="!finished" title="Close" class="i-button-close" @click.prevent="$emit('update:show', false)">
          <i-icon icon="times" size="medium"></i-icon>
        </i-button>
      </div>
    </template>
    <template #default>
      <div class="change-dates">
        <div class="change-dates-title">
          <i-icon type="far" icon="calendar" size="large" class="title-icon"></i-icon>
          <h1>{{ `${LANG.processingDates}: ${getProcessingString({ validDocuments, totalDocuments })}` }}</h1>
        </div>
        <div class="change-dates-controls" v-if="!showDocuments">
          <table class="filelist table">
            <tbody>
              <tr class="pointer date-container-row" v-if="this.checkACL('/document/publish_date')">
                <td>
                    <i-checkbox
                      :checked="publishDateEdit"
                      @click="publishDateEdit = !publishDateEdit"
                      :aria-label="LANG.checkboxDefaultAriaLabel"
                    />
                </td>
                <td class="date-label">
                  <label>{{ LANG.publishDate }}</label>
                </td>
                <td class="cal-container">
                  <div v-if="publishDateEdit">
                    <div v-if="!publishDateClearToggle">
                      <i-date-flatpickr v-model="publishDate" :locale="locale" />
                      <a class="del-mark" href="#" @click.stop="publishDateClearToggle = !publishDateClearToggle">
                        <i-icon type="far" icon="trash" size="small" class="title-icon"></i-icon>
                        {{ LANG.markForDeletion }}
                      </a>
                    </div>
                    <div v-else>
                      <span>{{ LANG.markedForDeletion }}<a class="del-mark" href="#" @click.stop="publishDateClearToggle = !publishDateClearToggle">({{ LANG.undo }})</a></span>
                    </div>
                  </div>
                  <span v-else>{{ LANG.checkToEdit }}</span>
                </td>
              </tr>
              <tr class="pointer date-container-row" v-if="this.checkACL('/document/expiry_date')">
                <td>
                    <i-checkbox
                      :checked="expiryDateEdit"
                      @click="expiryDateEdit = !expiryDateEdit"
                      :aria-label="LANG.checkboxDefaultAriaLabel"
                    />
                </td>
                <td class="date-label">
                  <label>{{ LANG.expiryDate }}</label>
                </td>
                <td class="cal-container">
                  <div v-if="expiryDateEdit">
                    <div v-if="!expiryDateClearToggle">
                      <i-date-flatpickr v-model="expiryDate" :locale="locale" />
                      <a class="del-mark" href="#" @click.stop="expiryDateClearToggle = !expiryDateClearToggle">
                        <i-icon type="far" icon="trash" size="small" class="title-icon"></i-icon>
                        {{ LANG.markForDeletion }}
                      </a>
                    </div>
                    <div v-else>
                      <span>{{ LANG.markedForDeletion }}<a class="del-mark" href="#" @click.stop="expiryDateClearToggle = !expiryDateClearToggle">({{ LANG.undo }})</a></span>
                    </div>
                  </div>
                  <span v-else>{{ LANG.checkToEdit }}</span>
                </td>
              </tr>
              <tr class="pointer date-container-row" v-if="this.checkACL('/document/resubmission_date')">
                <td>
                    <i-checkbox
                      :checked="resubmitDateEdit"
                      @click="resubmitDateEdit = !resubmitDateEdit"
                      :aria-label="LANG.checkboxDefaultAriaLabel"
                    />
                </td>
                <td class="date-label">
                  <label>{{ LANG.resubmitDate }}</label>
                </td>
                <td class="cal-container">
                  <div v-if="resubmitDateEdit">
                    <div v-if="!resubmitDateClearToggle">
                      <i-date-flatpickr v-model="resubmitDate" :locale="locale" />
                      <a class="del-mark" href="#" @click.stop="resubmitDateClearToggle = !resubmitDateClearToggle">
                        <i-icon type="far" icon="trash" size="small" class="title-icon"></i-icon>
                        {{ LANG.markForDeletion }}
                      </a>
                    </div>
                    <div v-else>
                      <span>{{ LANG.markedForDeletion }}<a class="del-mark" href="#" @click.stop="resubmitDateClearToggle = !resubmitDateClearToggle">({{ LANG.undo }})</a></span>
                    </div>
                  </div>
                  <span v-else>{{ LANG.checkToEdit }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="documents-wrapper" v-if='showDocuments'>
          <div v-for='(document) in documentsStatus'
            class="document-entry"
            :key="document.nodeId">
            <DocumentThumbnail
              :thumbnail="document.preview"
              :title="document.title"
              :alt="document.title"
              class="document-preview"
            />
            <div class="document-info">
              <span class="document-title" v-if="document.title">{{ document.title }}</span>
              <span class="document-title" v-else>{{ LANG.noDocumentTitle }}</span>
              <span class="document-status">
                <span v-if="document.status === 1">{{ LANG.cantEditDoc }}</span>
                <span v-else-if="document.message">{{ document.message }}</span>
                <span v-else>{{ LANG.scheduled }}</span>
              </span>
            </div>
            <div class="document-status-circle">
              <i-icon :class="statusClass(document.status)" :icon="statusIcon(document.status)" size="large"></i-icon>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-center gap-1">
        <i-button
          :disabled="saving"
          variant="secondary"
          @click.prevent="$emit('update:show', false)"
        >
          {{ finished ? LANG.close : LANG.cancel }}
        </i-button>
        <i-button
          v-if="!error && !finished && !showDocuments"
          :disabled="!canSave || saving"
          variant="secondary"
          @click.prevent="saveDates()"
        >
          {{ LANG.save }}
        </i-button>
      </div>
    </template>
  </i-modal>
</template>

<script>
import { defineComponent } from 'vue';
import { mapBrowserGetters, mapBrowserMutations } from '@/store';
import { mapState, mapGetters } from 'vuex';
import { Tree } from '@/utils/tree';
import fetchService from '@/utils/model/fetch_service';
import DocumentThumbnail from '@/components/browser/documents/DocumentThumbnail.vue';
import globals from '@/globals';

const DOCUMENT_STATUS = {
  IDLE: '0',
  WARNING: '1',
  ERROR: '2',
  SUCCESS: '3',
  LOADING: '4',
};

export default defineComponent({
  components: {
    DocumentThumbnail,
  },
  data: () => ({
    error: false,
    message: '',
    documentsStatus: {},
    finished: false,
    saving: false,
    documentsCount: 0,
    documents: 0,

    publishDate: new Date(),
    publishDateEdit: false,
    publishDateClearToggle: false,

    expiryDate: new Date(),
    expiryDateEdit: false,
    expiryDateClearToggle: false,

    resubmitDate: new Date(),
    resubmitDateEdit: false,
    resubmitDateClearToggle: false,

    showDocuments: false,
  }),
  props: {
    item: Object,
  },
  computed: {
    ...mapState(['LANG', 'browserMode', 'locale']),
    ...mapGetters(['checkACL']),
    ...mapBrowserGetters([
      'selectedDocuments',
      'selectedTreeNode',
      'tree',
    ]),
    canSave() {
      return Boolean(this.publishDateEdit || this.expiryDateEdit || this.resubmitDateEdit);
    },
    DOCUMENT_STATUS() {
      return DOCUMENT_STATUS;
    },
    totalDocuments() {
      return Object.keys(this.documentsStatus).length;
    },
    validDocuments() {
      if (!Object.keys(this.documentsStatus).length) return 0;

      return Object.values(this.documentsStatus).filter((d) => d.status === DOCUMENT_STATUS.SUCCESS).length;
    },
  },
  methods: {
    ...mapBrowserMutations([
      'setEntityTableDirty',
    ]),
    getProcessingString({ validDocuments, totalDocuments }) {
      return globals.I.gt.__x('{validDocuments} of {totalDocuments} documents', { validDocuments, totalDocuments });
    },
    getDateParams(date) {
      return {
        year: date.getFullYear(),
        mon: date.getMonth() + 1,
        day: date.getDate(),
        hour: date.getHours(),
        min: date.getMinutes(),
      };
    },
    async getDocumentsList() {
      const nodes = this.tree.nodes || [];
      this.finished = false;
      this.documentsStatus = {};
      this.showDocuments = false;
      Object.values(this.selectedDocuments).forEach((d) => {
        this.catNode = Tree.find(nodes, (n) => n.id === d.cat_id);
        this.documentsStatus[d.nodeId] = {
          nodeId: d.node_id,
          title: d.title,
          preview: d.preview_url,
          status: !this.catNode.can_edit ? '1' : '0',
        };
      });
    },
    async saveDates() {
      this.saving = true;
      this.documentsCount = 0;
      this.documents = this.validDocuments;
      const data = {};

      if (this.publishDateEdit) {
        data.publish = (!this.publishDateClearToggle)
          ? this.getDateParams(this.publishDate)
          : { clear: 1 };
      }
      if (this.expiryDateEdit) {
        data.expiry = (!this.expiryDateClearToggle)
          ? this.getDateParams(this.expiryDate)
          : { clear: 1 };
      }
      if (this.resubmitDateEdit) {
        data.resubmission = (!this.resubmitDateClearToggle)
          ? this.getDateParams(this.resubmitDate)
          : { clear: 1 };
      }

      const documents = Object.values(this.documentsStatus);
      await documents.reduce(async (promise, doc) => {
        await promise;
        await this.setDocumentDates(doc, data);
      }, Promise.resolve());

      this.setEntityTableDirty();
    },
    async setDocumentDates(doc, data) {
      this.documentsStatus[doc.nodeId].status = DOCUMENT_STATUS.LOADING;
      this.documentsStatus[doc.nodeId].message = this.LANG.loading;

      const response = await fetchService.GetData('docbrowser_change_dates', {
        node_id: doc.nodeId,
        dates: JSON.stringify(data),
      });

      // FIXME: status mapping
      this.documentsStatus[doc.nodeId].status = response.error ? DOCUMENT_STATUS.ERROR : DOCUMENT_STATUS.SUCCESS;
      this.documentsStatus[doc.nodeId].message = response.message;
      this.documentsCount += 1;
      this.showDocuments = true;

      if (this.documentsCount === this.totalDocuments) {
        this.saving = false;
        this.finished = true;
      }
    },
    statusIcon(status) {
      return {
        [DOCUMENT_STATUS.IDLE]: 'circle',
        [DOCUMENT_STATUS.WARNING]: 'exclamation-triangle',
        [DOCUMENT_STATUS.ERROR]: 'exclamation-circle',
        [DOCUMENT_STATUS.LOADING]: 'circle',
        [DOCUMENT_STATUS.SUCCESS]: 'check-circle',
      }[status];
    },
    statusClass(status) {
      return {
        [DOCUMENT_STATUS.IDLE]: '',
        [DOCUMENT_STATUS.WARNING]: 'yellow',
        [DOCUMENT_STATUS.ERROR]: 'red',
        [DOCUMENT_STATUS.LOADING]: 'loader',
        [DOCUMENT_STATUS.SUCCESS]: 'green',
      }[status];
    },
  },
});
</script>

<style scoped lang="scss">
.change-dates-controls {
  width: 90%;
}
.del-mark {
  color: #1c385f;
  cursor: pointer;
}
.date-container-row {
  height: 60px;
}
.cal-container {
  width: 400px;
}
.date-label label {
  margin-bottom: 0px;
}
.loading {
  height: 250px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.change-dates {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  .change-dates-title {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .title-icon {
      font-size: 48px;
    }
  }

  .documents-wrapper {
    .document-entry {
      display: flex;
      margin-top: 25px;

      .document-preview {
        border: 1px solid $gray;
        border-radius: 2px;
        align-self: center;
        height: 46px;
      }
      .document-info {
        display: flex;
        flex-direction: column;
        margin-left: 8px;
        min-width: 350px;
        width: 80%;

        .document-title {
          margin-top: 5px;
          font-weight: bold;
        }

        .document-status {
          color: $gray;
          font-size: 12px;
          margin-top: 5px;
        }
      }

      .document-status-circle {
        color: $gray;
        align-self: center;
        padding: 0 25px 0 25px;

        .loader {
          border: 2px solid transparent;
          border-top: 2px solid $blue;
          border-radius: 50%;
          animation: spin 2s linear infinite;
          margin: 2px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      }
    }
  }
}

</style>
